import {
  FailureCauseError,
  nativeErrorCause,
  type ExperimentProduct,
  type PaywallApi,
  type PaymentPlatform,
  type UserInfo,
  fetchExperimentProducts,
  mapPlatformToServiceApi,
} from '@forest/paywall-core'
import { compare } from 'compare-versions'
import pRetry from 'p-retry'
import { native } from '../native'
import {
  hasPageLoadingBudget,
  markNeverReplay,
} from '../page-loading-recovery-internal'
import { canonicalMock } from './canonical-mock'
import {
  readExpSkusCache,
  readSubscriptionProductsCache,
  writeExpSkusCache,
  writeSubscriptionProductsCache,
} from './cache'
import { getSubscriptionProductsBySkus, mergeProducts } from './merge'
import {
  MONTHLY_NO_TRIAL,
  buildMonthlyNoTrial,
  type DeployEnv,
} from './monthly-no-trial'
import type { Product, Products, SubscriptionProduct } from './types'

export interface GetProductsOptions {
  cacheKeyPrefix: string
  api: PaywallApi
  appVersion: string
  platform: PaymentPlatform
  deferredUserInfo: Promise<UserInfo>
  env: DeployEnv
  shouldMock: boolean
}

/**
 * Coordinate the existing product retry with the presentation's original page
 * budget instead of adding another retry layer: once the budget is spent (or
 * the page has ended) another attempt cannot land in time.
 *
 * The budget is module-global, so whether it applies has to be decided per
 * call, when the call begins. A read that begins inside a live page-loading
 * attempt is bounded by that attempt. A read that begins outside one — no
 * native presentation at all, or a page whose cover is already gone — keeps
 * p-retry's own policy, because there is no deadline for it to miss.
 */
function withinPageLoadingBudget(): { shouldRetry: () => boolean } {
  const boundedByAttempt = hasPageLoadingBudget()
  return {
    shouldRetry: () => !boundedByAttempt || hasPageLoadingBudget(),
  }
}

export async function getProducts(
  options: GetProductsOptions,
): Promise<Products> {
  try {
    return await runGetProducts(options)
  } catch (error) {
    // `getProducts` owns its own recovery: the p-retry legs above already
    // retried what is safe to retry, inside the same page budget. Once they
    // are spent, re-running the whole call would re-issue the native
    // subscription prefetch and re-hit the experiment endpoint every backoff
    // for the rest of the budget, so the page-loading wrapper must not loop on
    // it (spec Decision 5: one layer owns the retry). The caller still sees
    // its own error and its existing terminal policy applies.
    throw markNeverReplay(error)
  }
}

async function runGetProducts(options: GetProductsOptions): Promise<Products> {
  const {
    cacheKeyPrefix,
    api,
    appVersion,
    platform,
    deferredUserInfo,
    env,
    shouldMock,
  } = options

  if (shouldMock) return canonicalMock(env, platform)

  const supportsGetStoreRegion = compare(appVersion, '5.7.0', '>=')
  const supportsGetAvailableSubs = compare(appVersion, '5.2.0', '>=')

  // Cache A — subscription products (user-independent, only applicable on ≥5.2.0).
  const subsCacheKey = `${cacheKeyPrefix}.subs.${env}.${platform}`
  const cachedSubs = supportsGetAvailableSubs
    ? readSubscriptionProductsCache(subsCacheKey)
    : null

  // Fire native subscription fetch eagerly on miss; on hit, skip the native call entirely.
  const promiseSubscriptionProducts: Promise<SubscriptionProduct[]> | null =
    cachedSubs
      ? Promise.resolve(cachedSubs)
      : supportsGetAvailableSubs
        ? pRetry(async () => {
            const r = await native.iap.getAvailableSubscriptionProducts()
            if (!r.success) {
              throw new FailureCauseError(nativeErrorCause(r.error), r.error)
            }
            return r.data.products
          }, withinPageLoadingBudget())
        : null

  // Store region — eager, cheap, rejection-suppressed so unused-on-cache-hit is safe.
  const promiseStoreRegion: Promise<string | null> = supportsGetStoreRegion
    ? native.iap
        .getStoreRegion()
        .then((r) => (r.success ? r.data.region : null))
        .catch(() => null)
    : Promise.resolve(null)

  // Need user info to check exp cache and to call fetchExperimentProducts on miss.
  const userInfo = await deferredUserInfo

  // Cache B — experiment SKUs (per-user).
  const expCacheKey = `${cacheKeyPrefix}.exp.${userInfo.user_id}`
  const cachedExp = readExpSkusCache(expCacheKey)

  const promiseExpSkus: Promise<ExperimentProduct[]> = cachedExp
    ? Promise.resolve(cachedExp)
    : promiseStoreRegion.then((storeRegion) =>
        fetchExperimentProducts(api.service, {
          userId: userInfo.user_id,
          platform: mapPlatformToServiceApi(platform),
          storeRegion,
        }),
      )

  // For <5.2.0, subscription products must be synthesized from exp skus via getPrice.
  const finalSubscriptionProducts: Promise<SubscriptionProduct[]> =
    promiseSubscriptionProducts ??
    pRetry(async () => {
      const expSkus = await promiseExpSkus
      return getSubscriptionProductsBySkus(expSkus, platform, native.iap)
    }, withinPageLoadingBudget())

  const [expSkus, subscriptionProducts] = await Promise.all([
    promiseExpSkus,
    finalSubscriptionProducts,
  ])

  if (!cachedSubs && supportsGetAvailableSubs) {
    writeSubscriptionProductsCache(subsCacheKey, subscriptionProducts)
  }
  if (!cachedExp) writeExpSkusCache(expCacheKey, expSkus)

  const four = mergeProducts(expSkus, subscriptionProducts, platform)
  const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null
  const monthlyNoTrial: Product | null = noTrialConfig
    ? buildMonthlyNoTrial(noTrialConfig, subscriptionProducts)
    : null

  return { ...four, monthlyNoTrial }
}
